import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useActiveProject } from '@/contexts/ActiveProjectContext';

/**
 * Global Notes panel context.
 *
 * A single Notes surface that lives in the authenticated shell and survives
 * route changes. Any component can call `openPanel({ transcriptId, themeName })`
 * to bring it forward, pre-tagged with the current context.
 *
 * State (open + pinned) is persisted to localStorage per active project so the
 * researcher doesn't lose their place on refresh. On project switch, panel
 * closes and context resets — matches the cross-project isolation rule.
 */

export interface NotesPanelContextScope {
  transcriptId?: string | null;
  themeName?: string | null;
  transcriptTitle?: string | null;
}

interface NotesPanelContextValue {
  isOpen: boolean;
  pinned: boolean;
  scope: NotesPanelContextScope;
  openPanel: (scope?: NotesPanelContextScope) => void;
  closePanel: () => void;
  togglePin: () => void;
}

const NotesPanelContext = createContext<NotesPanelContextValue | undefined>(undefined);

const PIN_KEY_PREFIX = 'verithos.notes.pinned.';

export function NotesPanelProvider({ children }: { children: ReactNode }) {
  const { activeProjectId } = useActiveProject();
  const [isOpen, setIsOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [scope, setScope] = useState<NotesPanelContextScope>({});

  // Load pinned state for the active project. If pinned, auto-open.
  useEffect(() => {
    if (!activeProjectId) {
      setIsOpen(false);
      setPinned(false);
      setScope({});
      return;
    }
    try {
      const stored = localStorage.getItem(PIN_KEY_PREFIX + activeProjectId);
      const wasPinned = stored === 'true';
      setPinned(wasPinned);
      setIsOpen(wasPinned);
      setScope({});
    } catch {
      setPinned(false);
      setIsOpen(false);
    }
  }, [activeProjectId]);

  const openPanel = useCallback((nextScope?: NotesPanelContextScope) => {
    if (nextScope) setScope(nextScope);
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePin = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      if (activeProjectId) {
        try {
          if (next) localStorage.setItem(PIN_KEY_PREFIX + activeProjectId, 'true');
          else localStorage.removeItem(PIN_KEY_PREFIX + activeProjectId);
        } catch { /* localStorage unavailable */ }
      }
      return next;
    });
  }, [activeProjectId]);

  // Global keyboard shortcut: Cmd/Ctrl+Shift+N opens the panel.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        if (isOpen) setIsOpen(false);
        else openPanel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, openPanel]);

  return (
    <NotesPanelContext.Provider value={{ isOpen, pinned, scope, openPanel, closePanel, togglePin }}>
      {children}
    </NotesPanelContext.Provider>
  );
}

export function useNotesPanel() {
  const ctx = useContext(NotesPanelContext);
  if (!ctx) throw new Error('useNotesPanel must be used within a NotesPanelProvider');
  return ctx;
}
